import React, { useState, useRef, useMemo } from 'react';
import { useAppConfirm } from './AppDialog.jsx';
import { PRESETS } from '../data.js';
import { diffScenarios } from '../scenario-sig.js';
import { todayISO } from '../formatting.js';

const STORAGE_KEY = 'cma-dashboard-profiles-v7';

/* Signature of a scenario's loadable payload — stable JSON of everything
   the snapshot() function captures. Used to detect "dirty" state: if the
   user loaded profile X and then changed any scenario field, the active
   profile's signature no longer matches, so we surface "Update profile"
   vs "Save as new" actions. */
function signatureOf(profile) {
  if (!profile) return null;
  const { id: _i, name: _n, description: _d, savedAt: _s, ...payload } = profile;
  return stableStringify(payload);
}
function signatureOfScenario(scenario) {
  const { profiles: _p, activeProfile: _a, sectionDensity: _sd, ...rest } = scenario;
  return stableStringify(rest);
}
function stableStringify(obj) {
  return JSON.stringify(obj, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc; }, {});
    }
    return v;
  });
}

function persist(profiles) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles)); }
  catch { /* swallow — quota / SSR */ }
}

function downloadJSON(filename, payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const BTN = 'bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_PRIMARY = 'bg-[rgba(240,180,41,0.10)] border border-accent-gold text-accent-gold px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:bg-[rgba(240,180,41,0.18)]';
const BTN_DANGER = `${BTN} hover:!text-accent-red hover:!border-accent-red`;
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const HINT_WARN = `${HINT} !border-l-accent-gold !text-text`;

/* ProfileManager — save/load/duplicate/delete/export/import scenarios. */
export default function ProfileManager({ scenario, setScenario }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [importErr, setImportErr] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const { requestConfirmation, dialog: confirmDialog } = useAppConfirm();
  const fileRef = useRef(null);

  const profiles = scenario.profiles || {};
  /* v8 Path H — sort profile IDs by savedAt descending so the most
     recently saved profile appears on top. Falls back to alphabetical
     when savedAt is missing (legacy profiles pre-v7.2). */
  const profileIds = useMemo(
    () => Object.keys(profiles).sort((a, b) => {
      const sa = profiles[a]?.savedAt || '';
      const sb = profiles[b]?.savedAt || '';
      if (sa && sb) return sb.localeCompare(sa);
      if (sb && !sa) return 1;
      if (sa && !sb) return -1;
      return a.localeCompare(b);
    }),
    [profiles],
  );
  const active = scenario.activeProfile;
  const activeProfile = active ? profiles[active] : null;

  /* Compare stable signatures — skip unrelated scenario fields (profiles,
     active id, sectionDensity) so clicking around the UI doesn't falsely
     flag as dirty. */
  const isDirty = useMemo(() => {
    if (!activeProfile) return false;
    return signatureOf(activeProfile) !== signatureOfScenario(scenario);
  }, [activeProfile, scenario]);

  const flash = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 1800);
  };

  const writeProfiles = (next, activeId = active) => {
    persist(next);
    setScenario({ ...scenario, profiles: next, activeProfile: activeId });
  };

  /* `name` and `description` are explicitly stripped from the snapshot to
     prevent the scenario's preset `name` ("Base", "Stretch", …) from
     overwriting the user-supplied profile name when spread later. This
     bug silently turned every saved profile's displayed name into the
     preset name. */
  const snapshot = () => {
    const { profiles: _p, activeProfile: _a, sectionDensity: _sd, name: _n, description: _d, ...rest } = scenario;
    return { ...rest, savedAt: new Date().toISOString() };
  };

  const onSave = () => {
    const trimmed = name.trim();
    if (!trimmed) { flash('Name required'); return; }
    const id = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `profile-${Date.now()}`;
    const next = { ...profiles, [id]: {
      ...snapshot(),
      id,
      name: trimmed,
      description: description.trim() || undefined,
      /* Capture which preset the scenario was derived from at save time,
         so the user can tell saved profiles apart on the list even when
         they typed similar names. */
      basedOn: scenario.name || undefined,
    } };
    writeProfiles(next, id);
    setName('');
    setDescription('');
    flash(`Saved "${trimmed}"`);
  };

  /* Overwrite the currently-loaded profile with the current scenario.
     Keeps its id + name + description, refreshes savedAt + basedOn. */
  const onUpdateActive = () => {
    if (!activeProfile) return;
    const updated = {
      ...snapshot(),
      id: activeProfile.id,
      name: activeProfile.name,
      description: activeProfile.description,
      basedOn: scenario.name || activeProfile.basedOn || undefined,
    };
    const next = { ...profiles, [active]: updated };
    writeProfiles(next, active);
    flash(`Updated "${activeProfile.name}"`);
  };

  const onLoad = (id) => {
    const p = profiles[id];
    if (!p) return;
    const { id: _i, name: _n, savedAt: _s, ...payload } = p;
    /* Profiles saved under the v7.4 schema stored formalPeriodCount and
       formalPeriodDensity as per-tab maps ({cpa:1,cma:3,...} — legacy, or
       post-Phase-2 {actuals:1,budgets:3,...}). v8 uses a scalar. Coerce
       legacy maps → max value so projection math doesn't receive an
       object and return NaN (which silently empties every table in the
       app). */
    const periodScalar = (v, fallback) => {
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
      if (v && typeof v === 'object') {
        const vals = Object.values(v).filter(n => typeof n === 'number' && Number.isFinite(n) && n > 0);
        if (vals.length) return Math.max(...vals);
      }
      return fallback;
    };
    const migrated = {
      ...payload,
      multipliers: { marian: 1.0, ...(payload.multipliers ?? {}) },
      fxRates: payload.fxRates ?? {},
      formalPeriodCount:   periodScalar(payload.formalPeriodCount,   12),
      formalPeriodDensity: periodScalar(payload.formalPeriodDensity, 1),
    };
    setScenario({
      ...scenario,
      ...migrated,
      profiles,
      activeProfile: id,
    });
    flash(`Loaded "${p.name}"`);
  };

  const onDuplicate = (id) => {
    const p = profiles[id];
    if (!p) return;
    const newName = `${p.name} (copy)`;
    const newId = `${id}-copy-${Date.now().toString(36)}`;
    const next = { ...profiles, [newId]: { ...p, id: newId, name: newName, savedAt: new Date().toISOString() } };
    writeProfiles(next, newId);
    flash(`Duplicated as "${newName}"`);
  };

  const onDelete = async (id) => {
    const p = profiles[id];
    if (!p) return;
    const ok = await requestConfirmation({
      title: `Delete profile "${p.name}"?`,
      message: 'This removes the private browser profile from this device. Export it first if you need a backup.',
      confirmLabel: 'Delete profile',
      tone: 'danger',
    });
    if (!ok) return;
    const next = { ...profiles };
    delete next[id];
    const newActive = active === id ? null : active;
    writeProfiles(next, newActive);
    flash(`Deleted "${p.name}"`);
  };

  const onExport = (id) => {
    const p = profiles[id];
    if (!p) return;
    downloadJSON(`cleic-lens-private-profile-${p.id}.json`, p);
  };

  const onExportAll = () => {
    downloadJSON(`cleic-lens-private-profiles-${todayISO()}.json`, profiles);
  };

  const onImport = (e) => {
    setImportErr(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        let next = { ...profiles };
        let count = 0;
        const ingest = (p) => {
          if (!p || typeof p !== 'object' || !p.id) return;
          next[p.id] = { ...p, savedAt: p.savedAt || new Date().toISOString() };
          count++;
        };
        if (parsed && parsed.id) ingest(parsed);
        else if (parsed && typeof parsed === 'object') Object.values(parsed).forEach(ingest);
        if (count === 0) { setImportErr('No valid profiles in file.'); return; }
        writeProfiles(next, active);
        flash(`Imported ${count} profile${count === 1 ? '' : 's'}`);
      } catch (err) {
        setImportErr(`Parse error: ${err.message}`);
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  return (
    <div className="flex flex-col gap-[0.6rem]">
      <div className="flex gap-[0.4rem] items-start flex-wrap">
        <div className="flex flex-col gap-[0.3rem] flex-1 min-w-[240px]">
          <input
            type="text"
            className="bg-surface-raised border border-border-strong text-text px-[0.6rem] py-[0.4rem] rounded-sm font-[inherit] placeholder:text-text-dim placeholder:italic placeholder:opacity-60"
            placeholder="Profile name (e.g. 'Conservative + no launch')"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) onSave(); }}
            aria-label="Profile name"
          />
          <input
            type="text"
            className="bg-surface-raised border border-border-strong text-text px-[0.6rem] py-[0.4rem] rounded-sm font-[inherit] text-[0.82rem] placeholder:text-text-dim placeholder:italic placeholder:opacity-60"
            placeholder="Description — what is this model state for? (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) onSave(); }}
            aria-label="Profile description"
          />
          <AutoDiffHint
            baseline={activeProfile || PRESETS.base}
            scenario={scenario}
            baselineLabel={activeProfile ? activeProfile.name : 'Default · Base'}
            onUseAsDescription={(text) => setDescription(text)}
          />
        </div>
        <button type="button" className={BTN_PRIMARY} onClick={onSave}>
          Save
        </button>
      </div>

      {feedback && <p className="text-[0.78rem] text-accent-green m-0">{feedback}</p>}

      {activeProfile && (
        <div className={`flex items-center gap-2 flex-wrap px-[0.7rem] py-[0.5rem] rounded-sm border ${isDirty ? 'bg-[rgba(240,180,41,0.08)] border-accent-gold' : 'bg-[rgba(74,222,128,0.06)] border-[rgba(74,222,128,0.3)]'}`}>
          <span className="text-[0.82rem] text-text">
            {isDirty ? '●' : '✓'} <strong className="text-text">{activeProfile.name}</strong>
            <span className="text-text-muted"> · {isDirty ? 'modified since load' : 'in sync'}</span>
          </span>
          {isDirty && (
            <div className="flex gap-[0.3rem] ml-auto flex-wrap">
              <button type="button" className={BTN_PRIMARY} onClick={onUpdateActive} title={`Overwrite "${activeProfile.name}" with current values`}>
                Update "{activeProfile.name}"
              </button>
              <button type="button" className={BTN} onClick={() => setName(`${activeProfile.name} v2`)} title="Fill the name field with a suggested new name so you can save as a new profile">
                Save as new…
              </button>
            </div>
          )}
        </div>
      )}

      {profileIds.length === 0 ? (
        <p className={HINT}>No saved private browser profiles yet. Tweak the model state, then save it with a name above.</p>
      ) : (
        <ul className="list-none p-0 m-0 flex flex-col gap-[0.3rem]">
          {profileIds.map((id) => {
            const p = profiles[id];
            const isActive = active === id;
            const rowState = isActive ? 'border-accent-gold' : 'border-border';
            return (
              <li key={id} className={`flex justify-between items-start px-[0.7rem] py-[0.5rem] bg-surface-el border rounded-sm gap-[0.6rem] flex-wrap ${rowState}`}>
                <div className="flex flex-col leading-[1.3] min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <strong className="text-[0.88rem] text-text">{p.name}</strong>
                    {p.basedOn && (
                      <span className="text-[0.65rem] uppercase tracking-[0.08em] text-accent-gold bg-[rgba(240,180,41,0.10)] border border-[rgba(240,180,41,0.35)] rounded-[3px] px-[6px] py-[1px] font-semibold">
                        {p.basedOn}
                      </span>
                    )}
                  </div>
                  {p.description && (
                    <span className="text-[0.78rem] text-text-muted mt-[0.15rem] break-words">{p.description}</span>
                  )}
                  <em className="text-[0.7rem] text-text-dim not-italic mt-[0.15rem]">{p.savedAt?.slice(0, 10) || '—'}{isActive ? ' · active' : ''}</em>
                </div>
                <div className="flex gap-[0.3rem] flex-wrap">
                  <button type="button" className={BTN} onClick={() => onLoad(id)}>Load</button>
                  <button type="button" className={BTN} onClick={() => onDuplicate(id)}>Duplicate</button>
                  <button type="button" className={BTN} onClick={() => onExport(id)}>Export private JSON</button>
                  <button type="button" className={BTN_DANGER} onClick={() => onDelete(id)}>Delete</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex gap-[0.4rem] items-center flex-wrap pt-[0.4rem] border-t border-dashed border-border">
        <button
          type="button"
          className={BTN}
          onClick={() => fileRef.current?.click()}
          title="Import private browser JSON; review/redact before sharing outside this device"
        >
          Import private JSON…
        </button>
        <button
          type="button"
          className={BTN}
          onClick={onExportAll}
          disabled={profileIds.length === 0}
        >
          Export all private JSON
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={onImport}
        />
      </div>
      <p className={HINT}>
        Profiles are private browser JSON. Import/export does not write to Ledger or any provider, and shared files should be reviewed and redacted first.
      </p>
      {importErr && <p className={HINT_WARN}>⚠ {importErr}</p>}
      {confirmDialog}
    </div>
  );
}

/* v8 Path F — AutoDiffHint
   Shows a 1-liner auto-generated summary of what's changed vs the
   baseline (either loaded profile or Default · Base). Click to paste
   into the description field so the operator doesn't need to hand-write the
   change. Collapses cleanly when nothing has changed. */
function AutoDiffHint({ baseline, scenario, baselineLabel, onUseAsDescription }) {
  const summary = useMemo(() => {
    const changes = diffScenarios(baseline, scenario);
    if (changes.length === 0) return null;
    const paths = changes.slice(0, 4).map(c => c.path);
    const more = changes.length > 4 ? ` +${changes.length - 4}` : '';
    return {
      text: `${changes.length} change${changes.length === 1 ? '' : 's'} from ${baselineLabel}: ${paths.join(', ')}${more}`,
      details: changes.map(c => `${c.path}: ${c.summary}`).join(' · '),
    };
  }, [baseline, scenario, baselineLabel]);
  if (!summary) return null;
  return (
    <div className="flex items-center gap-[0.5rem] px-[0.4rem] py-[0.25rem] bg-[rgba(240,180,41,0.05)] border border-[rgba(240,180,41,0.25)] rounded-sm">
      <span className="text-[0.72rem] text-text-muted leading-snug flex-1 min-w-0 truncate" title={summary.details}>
        💡 {summary.text}
      </span>
      <button
        type="button"
        className="bg-transparent border border-accent-gold/60 text-accent-gold px-[0.45rem] py-[0.1rem] rounded-sm cursor-pointer text-[0.7rem] font-[inherit] whitespace-nowrap hover:bg-[rgba(240,180,41,0.10)]"
        onClick={() => onUseAsDescription(summary.details)}
        title="Paste the full diff into the description field"
      >
        Use as description
      </button>
    </div>
  );
}
